/**
 * TopBarC — the pane's context bar (design board "Account menu", 39).
 *
 * One 48px row above the tabs:
 *   - Brand mark (left)
 *   - Status pill: Live / Dev / Sandbox. A button only when internal tools
 *     are on and a mode flip is safe; read-only otherwise.
 *   - Context chips (doctype · library · workspace), each opening its picker
 *   - Account avatar (right) opening the account menu
 *
 * The account menu carries the signed-in identity, the token meter and the
 * account actions (document settings, billing, Trust Center, help, change
 * password, save now, sign out).
 *
 * SRP: pure presentation. Receives data via props; emits intents via
 * callbacks. The only fetch is the token quota, read when the menu opens.
 */

import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { getMyQuota } from "../../api/client";
import { brand } from "../../config/brand";
import { type ContextChip, ContextBar, type PillStatus, ProgressBar } from "../../ds/components";
import { deriveDisplayFirstName, deriveInitials } from "../homeSceneMapping";
import { openExternal } from "../services/openExternal";

// Trust Center and support — same verified-live marketing host used across the
// app. Opened via openExternal (system browser) so the Office task-pane WebView
// doesn't drop them.
const TRUST_URL = "https://asthra-writer.ai/trust";
const SUPPORT_URL = "https://asthra-writer.ai/support";

export type TopBarMode = "live" | "sandbox";

interface TopBarCProps {
  /** Sandbox vs Live — drives the status pill. Reflects backend config
   *  (fetched via getAppConfig), not a hard-coded value. */
  mode: TopBarMode;
  /** Optional mode-flip intent. Used only when ``showInternalTools`` is on;
   *  the host omits it when toggling would be unsafe (mid-run / approval). */
  onModeToggle?: () => void;
  /** True while a mode-flip request is in flight — the pill stops being a
   *  button so a double-click can't fire twice. */
  modeToggling?: boolean;
  /** Sign-out intent. When omitted, the Sign out item is hidden. */
  onLogout?: () => void;
  /** Open the Billing & usage surface. Menu item rendered when provided. */
  onOpenBilling?: () => void;
  /** Open the Change password dialog. Menu item rendered when provided. */
  onChangePassword?: () => void;
  /** Re-show the first-run introduction ("How it works"). */
  onShowIntro?: () => void;
  /** Open the document settings (doctype + region). */
  onDocumentSettings?: () => void;
  /** Trailing hint on the Document settings item, e.g. "CSR · US". */
  documentSettingsHint?: string | null;
  /** Manual save. Edits auto-save; this is the explicit flush. */
  onSave?: () => void;
  /** Disables "Save now" (e.g. while a draft is in flight). */
  saveDisabled?: boolean;
  /** Logged-in user's identifier (typically the email used at login).
   *  Drives the avatar initials and the menu header. */
  userIdentifier?: string | null;
  /** Whether the status pill may flip the backend mode. Defaults to
   *  ``false``: ``useFlag`` returns false while flags load, on error and for
   *  unknown keys, so "act when true" keeps the switch hidden in all three. */
  showInternalTools?: boolean;
  /** True when this bundle came from the Vite dev server (``pnpm dev``).
   *  The live pill then reads "Dev" so a local stack is never mistaken for
   *  production. Sandbox keeps its own label. */
  devBuild?: boolean;
  /** Live token counts accumulated during an active run, added to the
   *  persisted totals in the token meter. */
  liveTokens?: {
    input: number;
    output: number;
    thinking: number;
  } | null;
  /** Context chips (doctype · library · workspace). The shell omits them
   *  during takeovers and on Home. */
  chips?: readonly ContextChip[];
}

interface Quota {
  used: number;
  limit: number | null;
}

export function formatTokenCount(count: number): string {
  const short = (n: number) => n.toFixed(1).replace(/\.0$/, "");
  if (count >= 1_000_000) return `${short(count / 1_000_000)}M`;
  if (count >= 1_000) return `${short(count / 1_000)}K`;
  return count.toString();
}

export const TopBarC: React.FC<TopBarCProps> = ({
  mode,
  onModeToggle,
  modeToggling,
  onLogout,
  onOpenBilling,
  onChangePassword,
  onShowIntro,
  onDocumentSettings,
  documentSettingsHint,
  onSave,
  saveDisabled = false,
  userIdentifier,
  showInternalTools = false,
  devBuild = false,
  liveTokens,
  chips = [],
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [quota, setQuota] = useState<Quota | null>(null);

  const status: PillStatus = mode === "sandbox" ? "paused" : "live";
  const statusLabel = mode === "sandbox" ? "Sandbox" : devBuild ? "Dev" : "Live";
  const canToggle = showInternalTools && !!onModeToggle && !modeToggling;

  // Quota is read when the menu opens, so the meter is current without a timer.
  useEffect(() => {
    if (!menuOpen) return;
    let alive = true;
    getMyQuota()
      .then((q) => {
        if (!alive) return;
        const limit =
          q.input_tokens_limit != null && q.output_tokens_limit != null
            ? q.input_tokens_limit + q.output_tokens_limit
            : null;
        setQuota({ used: q.input_tokens_used + q.output_tokens_used, limit });
      })
      .catch((err) => console.error("Failed to fetch quota:", err));
    return () => {
      alive = false;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const run = useCallback((fn: () => void) => {
    setMenuOpen(false);
    fn();
  }, []);

  const live = liveTokens ? liveTokens.input + liveTokens.output : 0;
  const used = (quota?.used ?? 0) + live;
  const name = deriveDisplayFirstName(userIdentifier) ?? "Account";
  const initials = deriveInitials(userIdentifier) ?? "·";
  // Asthra uses the design-system burst; white-label brands keep their image.
  const mark =
    brand.logoMark && brand.wordmark ? undefined : (
      <img src={brand.logoMark ?? brand.logoHorizontal} alt={brand.name} className="as-ctx-mark" />
    );

  return (
    <div className="as-topbar">
      <ContextBar
        status={status}
        statusLabel={statusLabel}
        onStatusClick={canToggle ? onModeToggle : undefined}
        chips={chips}
        initials={initials}
        onAccount={() => setMenuOpen((o) => !o)}
        accountExpanded={menuOpen}
        mark={mark}
      />
      {menuOpen && (
        <>
          <div className="as-acct-scrim" aria-hidden="true" onClick={() => setMenuOpen(false)} />
          <nav className="as-acct" aria-label="Account">
            <div className="as-acct-who">
              <span className="as-acct-name">{name}</span>
              {userIdentifier ? <span className="as-acct-email">{userIdentifier}</span> : null}
            </div>
            <div className="as-acct-meter">
              <ProgressBar
                value={quota?.limit ? used : 0}
                total={quota?.limit ?? 0}
                label={
                  quota
                    ? quota.limit
                      ? `Tokens used · ${formatTokenCount(used)} of ${formatTokenCount(quota.limit)}`
                      : `Tokens used · ${formatTokenCount(used)}`
                    : "Tokens used · …"
                }
              />
            </div>
            <div role="menu" aria-label="Account actions" className="as-acct-items">
              {onDocumentSettings && (
                <button type="button" role="menuitem" className="as-mi" onClick={() => run(onDocumentSettings)}>
                  Document settings{documentSettingsHint ? <small>{documentSettingsHint}</small> : null}
                </button>
              )}
              <div className="as-mi as-mi-static" role="menuitem" aria-disabled="true">
                Appearance<small>Follow Word</small>
              </div>
              {onOpenBilling && (
                <button type="button" role="menuitem" className="as-mi" onClick={() => run(onOpenBilling)}>
                  Billing &amp; usage
                </button>
              )}
              <div className="as-mi-sep" role="separator" />
              <button
                type="button"
                role="menuitem"
                className="as-mi"
                onClick={() => run(() => openExternal(TRUST_URL))}
              >
                Trust Center
              </button>
              {onShowIntro && (
                <button type="button" role="menuitem" className="as-mi" onClick={() => run(onShowIntro)}>
                  How it works
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="as-mi"
                onClick={() => run(() => openExternal(SUPPORT_URL))}
              >
                Help &amp; support
              </button>
              {onChangePassword && (
                <button type="button" role="menuitem" className="as-mi" onClick={() => run(onChangePassword)}>
                  Change password
                </button>
              )}
              {onSave && (
                <button
                  type="button"
                  role="menuitem"
                  className="as-mi"
                  disabled={saveDisabled}
                  onClick={() => run(onSave)}
                >
                  Save now
                </button>
              )}
              {onLogout && (
                <>
                  <div className="as-mi-sep" role="separator" />
                  <button type="button" role="menuitem" className="as-mi as-mi-danger" onClick={() => run(onLogout)}>
                    Sign out
                  </button>
                </>
              )}
            </div>
          </nav>
        </>
      )}
    </div>
  );
};

export default TopBarC;
